import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, Android, DeleteOutline, PlayArrow, SaveOutlined } from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const initialBot = {
  name: "Asistente comercial",
  active: false,
  welcomeMessage: "¡Hola! Soy el asistente virtual. ¿Cómo puedo ayudarte?",
  menuPrompt: "Elegí una opción para continuar:",
  fallbackMessage: "No entendí tu respuesta. Elegí una opción disponible o escribí 'asesor'.",
  options: [
    { label: "Consultar pedido", reply: "Perfecto. Un asesor revisará el estado de tu pedido." },
    { label: "Hablar con un asesor", reply: "Te derivamos con el equipo para continuar la atención." },
  ],
};

const useStyles = makeStyles((theme) => ({
  root: { height: "100%", overflow: "auto", padding: theme.spacing(2), background: theme.palette.background.default },
  header: { justifyContent: "space-between", marginBottom: theme.spacing(2) },
  layout: { display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(300px, .85fr)", gap: theme.spacing(2), alignItems: "start", "@media (max-width: 900px)": { gridTemplateColumns: "1fr" } },
  section: { padding: theme.spacing(2), borderRadius: 14 },
  hint: { marginTop: 2, color: theme.palette.text.secondary },
  fields: { display: "grid", gap: theme.spacing(1.5), marginTop: theme.spacing(2) },
  option: { display: "grid", gridTemplateColumns: "minmax(120px, .34fr) minmax(0, 1fr) auto", gap: theme.spacing(1), alignItems: "center", padding: theme.spacing(1), border: `1px solid ${theme.palette.divider}`, borderRadius: 10, "@media (max-width: 620px)": { gridTemplateColumns: "1fr auto", "& > :nth-child(2)": { gridColumn: "1" } } },
  actions: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: theme.spacing(1), flexWrap: "wrap", marginTop: theme.spacing(2) },
  safeNotice: { padding: theme.spacing(1.25), borderRadius: 10, background: theme.palette.type === "dark" ? "#193a34" : "#ecfdf5", color: theme.palette.type === "dark" ? "#b7f7dc" : "#166534", fontWeight: 600 },
  preview: { background: theme.palette.type === "dark" ? "#122420" : "#f7fbff", minHeight: 455, display: "flex", flexDirection: "column" },
  previewHeader: { display: "flex", alignItems: "center", gap: theme.spacing(1), padding: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.divider}` },
  messageArea: { flex: 1, padding: theme.spacing(2), display: "flex", flexDirection: "column", gap: theme.spacing(1), overflow: "auto" },
  message: { alignSelf: "flex-start", maxWidth: "88%", padding: theme.spacing(1.1, 1.25), borderRadius: "14px 14px 14px 4px", background: theme.palette.type === "dark" ? "#274942" : "#e8f2ff" },
  menu: { display: "flex", flexDirection: "column", gap: theme.spacing(.75), padding: theme.spacing(0, 2, 2) },
  status: { fontWeight: 700 },
}));

const Chatbot = () => {
  const classes = useStyles();
  const { isAuth } = useContext(AuthContext);
  const [botId, setBotId] = useState(null);
  const [bot, setBot] = useState(initialBot);
  const [preview, setPreview] = useState([]);

  const loadBot = async () => {
    try {
      const { data } = await api.get("/crm/chatbots/");
      const saved = data.chatbots?.[0];
      if (saved) {
        setBotId(saved.id);
        setBot({ ...initialBot, ...saved, options: saved.options?.length ? saved.options : initialBot.options });
      }
    } catch (error) { toastError(error); }
  };

  useEffect(() => { if (isAuth) loadBot(); }, [isAuth]);

  const previewMessages = useMemo(() => preview.length ? preview : [
    { id: "welcome", body: bot.welcomeMessage },
    { id: "menu", body: bot.menuPrompt },
  ], [preview, bot.welcomeMessage, bot.menuPrompt]);

  const updateOption = (index, field, value) => setBot((current) => ({
    ...current,
    options: current.options.map((option, itemIndex) => itemIndex === index ? { ...option, [field]: value } : option),
  }));

  const saveBot = async () => {
    try {
      const payload = { ...bot, options: bot.options.filter((option) => option.label.trim()) };
      const response = botId ? await api.put(`/crm/chatbots/${botId}`, payload) : await api.post("/crm/chatbots", payload);
      setBotId(response.data.id);
      setBot({ ...initialBot, ...response.data });
      toast.success("Configuración del chatbot guardada.");
    } catch (error) { toastError(error); }
  };

  const testBot = () => setPreview([{ id: "welcome", body: bot.welcomeMessage }, { id: "menu", body: bot.menuPrompt }]);
  const selectOption = (option) => setPreview((messages) => [
    ...messages,
    { id: `option-${Date.now()}`, body: `Cliente: ${option.label}`, own: true },
    { id: `reply-${Date.now()}`, body: option.reply || bot.fallbackMessage },
  ]);

  return <MainContainer><div className={classes.root}>
    <MainHeader className={classes.header}>
      <div><Title>Chatbot</Title><Typography className={classes.hint}>Configurá el asistente para los canales conectados y probalo internamente.</Typography></div>
      <Chip className={classes.status} color={bot.active ? "primary" : "default"} label={bot.active ? "Configuración activa" : "En borrador"} />
    </MainHeader>
    <div className={classes.layout}>
      <Paper variant="outlined" className={classes.section}>
        <div className={classes.safeNotice}>Las respuestas automáticas están desactivadas. Esta pantalla solo guarda la configuración y permite pruebas internas.</div>
        <div className={classes.fields}>
          <TextField label="Nombre del chatbot" value={bot.name} onChange={(event) => setBot({ ...bot, name: event.target.value })} fullWidth />
          <FormControlLabel control={<Switch color="primary" checked={bot.active} onChange={(event) => setBot({ ...bot, active: event.target.checked })} />} label="Guardar como configuración activa para una futura habilitación" />
          <TextField label="Mensaje de bienvenida" value={bot.welcomeMessage} onChange={(event) => setBot({ ...bot, welcomeMessage: event.target.value })} multiline minRows={2} fullWidth />
          <TextField label="Pregunta o menú" value={bot.menuPrompt} onChange={(event) => setBot({ ...bot, menuPrompt: event.target.value })} multiline minRows={2} fullWidth />
          <Typography variant="subtitle1" style={{ fontWeight: 800 }}>Opciones del menú</Typography>
          {bot.options.map((option, index) => <div className={classes.option} key={index}>
            <TextField label="Opción" value={option.label} onChange={(event) => updateOption(index, "label", event.target.value)} />
            <TextField label="Respuesta" value={option.reply} onChange={(event) => updateOption(index, "reply", event.target.value)} />
            <IconButton aria-label="Eliminar opción" onClick={() => setBot((current) => ({ ...current, options: current.options.filter((_, itemIndex) => itemIndex !== index) }))}><DeleteOutline /></IconButton>
          </div>)}
          <Button startIcon={<Add />} onClick={() => setBot((current) => ({ ...current, options: [...current.options, { label: "", reply: "" }] }))}>Agregar opción</Button>
          <TextField label="Respuesta cuando no se reconoce el mensaje" value={bot.fallbackMessage} onChange={(event) => setBot({ ...bot, fallbackMessage: event.target.value })} multiline minRows={2} fullWidth />
        </div>
        <Divider style={{ marginTop: 18 }} />
        <div className={classes.actions}><Button startIcon={<PlayArrow />} onClick={testBot}>Probar internamente</Button><Button startIcon={<SaveOutlined />} color="primary" variant="contained" onClick={saveBot}>Guardar chatbot</Button></div>
      </Paper>
      <Paper variant="outlined" className={`${classes.section} ${classes.preview}`}>
        <div className={classes.previewHeader}><Android color="primary" /><div><Typography style={{ fontWeight: 800 }}>{bot.name || "Chatbot"}</Typography><Typography variant="caption" color="textSecondary">Vista previa interna</Typography></div></div>
        <div className={classes.messageArea}>{previewMessages.filter((message) => message.body).map((message) => <div key={message.id} className={classes.message} style={message.own ? { alignSelf: "flex-end", borderRadius: "14px 14px 4px 14px", background: "#2fb87b", color: "#fff" } : undefined}>{message.body}</div>)}</div>
        <div className={classes.menu}>{bot.options.filter((option) => option.label.trim()).map((option, index) => <Button key={`${option.label}-${index}`} variant="outlined" color="primary" onClick={() => selectOption(option)}>{option.label}</Button>)}</div>
      </Paper>
    </div>
  </div></MainContainer>;
};

export default Chatbot;
